import React, { useCallback, useState } from "react";
import "./SecurityCTA.css";

const API_BASE = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(
  /\/+$/,
  ""
);

const AUTH_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const initialGeneralForm = {
  fullName: "",
  email: "",
  phone: "",
  domainName: "",
  securityType: "general",
  message: "",
};

const initialSslForm = {
  fullName: "",
  email: "",
  phone: "",
  domainName: "",
  message: "",
};

const initialAccountForm = {
  fullName: "",
  email: "",
  phone: "",
  domainName: "",
  message: "",
};

const initialDomainLockForm = {
  fullName: "",
  email: "",
  phone: "",
  domainName: "",
  message: "",
};

const getToken = () => {
  for (const key of AUTH_KEYS) {
    const value = localStorage.getItem(key) || sessionStorage.getItem(key);
    if (value && String(value).trim()) return String(value).trim();
  }

  return "";
};

const clearAuthStorage = () => {
  AUTH_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  window.dispatchEvent(new Event("auth-state-changed"));
};

const buildHeaders = () => {
  const token = getToken();

  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const isValidEmail = (email = "") => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isValidDomain = (value = "") => {
  const domain = value.trim().toLowerCase();

  if (!domain) return true;

  return /^(?!-)([a-z0-9-]{1,63}\.)+[a-z]{2,63}$/.test(domain);
};

const normalizePayload = (form = {}) => ({
  fullName: form.fullName.trim(),
  email: form.email.trim().toLowerCase(),
  phone: form.phone.trim(),
  domainName: form.domainName.trim().toLowerCase(),
  ...(form.securityType ? { securityType: form.securityType } : {}),
  message: form.message.trim(),
  sourcePage: "security",
});

function SecurityCTA() {
  const [generalForm, setGeneralForm] = useState(initialGeneralForm);
  const [sslForm, setSslForm] = useState(initialSslForm);
  const [accountForm, setAccountForm] = useState(initialAccountForm);
  const [domainLockForm, setDomainLockForm] = useState(initialDomainLockForm);

  const [generalLoading, setGeneralLoading] = useState(false);
  const [sslLoading, setSslLoading] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [domainLockLoading, setDomainLockLoading] = useState(false);

  const [generalStatus, setGeneralStatus] = useState({ type: "", message: "" });
  const [sslStatus, setSslStatus] = useState({ type: "", message: "" });
  const [accountStatus, setAccountStatus] = useState({ type: "", message: "" });
  const [domainLockStatus, setDomainLockStatus] = useState({
    type: "",
    message: "",
  });

  const handleFormChange = (setter) => (event) => {
    const { name, value } = event.target;

    setter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateBaseForm = (form, options = {}) => {
    const { domainRequired = false, messageRequired = true } = options;

    if (!form.fullName.trim()) return "Full name is required.";

    if (!isValidEmail(form.email.trim())) {
      return "Please enter a valid email address.";
    }

    if (domainRequired && !form.domainName.trim()) {
      return "Domain name is required.";
    }

    if (!isValidDomain(form.domainName)) {
      return "Please enter a valid domain name.";
    }

    if (messageRequired && !form.message.trim()) {
      return "Message is required.";
    }

    return "";
  };

  const postRequest = useCallback(async (url, payload) => {
    const response = await fetch(`${API_BASE}${url}`, {
      method: "POST",
      headers: buildHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (response.status === 401) {
      clearAuthStorage();
      throw new Error("Session expired. Please login again.");
    }

    if (!response.ok || !data?.success) {
      throw new Error(data?.message || "Request failed.");
    }

    return data;
  }, []);

  const handleGeneralSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateBaseForm(generalForm, {
      domainRequired: false,
      messageRequired: true,
    });

    setGeneralStatus({ type: "", message: "" });

    if (validationError) {
      setGeneralStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setGeneralLoading(true);

      const data = await postRequest(
        "/api/security/enquiry",
        normalizePayload(generalForm)
      );

      setGeneralStatus({
        type: "success",
        message: data.message || "Security enquiry submitted successfully.",
      });

      setGeneralForm(initialGeneralForm);
      window.dispatchEvent(new Event("security-request-created"));
    } catch (error) {
      setGeneralStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setGeneralLoading(false);
    }
  };

  const handleSslSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateBaseForm(sslForm, {
      domainRequired: true,
      messageRequired: false,
    });

    setSslStatus({ type: "", message: "" });

    if (validationError) {
      setSslStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setSslLoading(true);

      const data = await postRequest(
        "/api/security/ssl-request",
        normalizePayload(sslForm)
      );

      setSslStatus({
        type: "success",
        message: data.message || "SSL request submitted successfully.",
      });

      setSslForm(initialSslForm);
      window.dispatchEvent(new Event("security-request-created"));
    } catch (error) {
      setSslStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setSslLoading(false);
    }
  };

  const handleAccountSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateBaseForm(accountForm, {
      domainRequired: false,
      messageRequired: true,
    });

    setAccountStatus({ type: "", message: "" });

    if (validationError) {
      setAccountStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setAccountLoading(true);

      const data = await postRequest(
        "/api/security/account-protection",
        normalizePayload(accountForm)
      );

      setAccountStatus({
        type: "success",
        message:
          data.message || "Account protection request submitted successfully.",
      });

      setAccountForm(initialAccountForm);
      window.dispatchEvent(new Event("security-request-created"));
    } catch (error) {
      setAccountStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setAccountLoading(false);
    }
  };

  const handleDomainLockSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateBaseForm(domainLockForm, {
      domainRequired: true,
      messageRequired: false,
    });

    setDomainLockStatus({ type: "", message: "" });

    if (validationError) {
      setDomainLockStatus({ type: "error", message: validationError });
      return;
    }

    try {
      setDomainLockLoading(true);

      const data = await postRequest(
        "/api/security/domain-lock",
        normalizePayload(domainLockForm)
      );

      setDomainLockStatus({
        type: "success",
        message: data.message || "Domain lock request submitted successfully.",
      });

      setDomainLockForm(initialDomainLockForm);
      window.dispatchEvent(new Event("security-request-created"));
    } catch (error) {
      setDomainLockStatus({
        type: "error",
        message: error.message || "Something went wrong.",
      });
    } finally {
      setDomainLockLoading(false);
    }
  };

  return (
    <section className="scta-section">
      <div className="scta-container">
        <div className="scta-header">
          <span className="scta-badge">Get Protected</span>

          <h2>Request Security Services for Your Website and Account</h2>

          <p>
            Submit security enquiries, SSL requests, account protection requests,
            and domain lock requests with safe, duplicate-controlled data handling.
          </p>
        </div>

        <div className="scta-grid">
          <div className="scta-card" id="security-main-form">
            <h3>General Security Enquiry</h3>
            <p>Ask for help with security setup, website protection, or general support.</p>

            <form onSubmit={handleGeneralSubmit} className="scta-form">
              <div className="scta-form-group">
                <label htmlFor="security-general-full-name">Full Name</label>
                <input
                  id="security-general-full-name"
                  type="text"
                  name="fullName"
                  value={generalForm.fullName}
                  onChange={handleFormChange(setGeneralForm)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-general-email">Email Address</label>
                <input
                  id="security-general-email"
                  type="email"
                  name="email"
                  value={generalForm.email}
                  onChange={handleFormChange(setGeneralForm)}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-general-phone">Phone Number</label>
                <input
                  id="security-general-phone"
                  type="text"
                  name="phone"
                  value={generalForm.phone}
                  onChange={handleFormChange(setGeneralForm)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-general-domain">Domain Name</label>
                <input
                  id="security-general-domain"
                  type="text"
                  name="domainName"
                  value={generalForm.domainName}
                  onChange={handleFormChange(setGeneralForm)}
                  placeholder="example.com"
                  autoComplete="off"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-general-type">Security Type</label>
                <select
                  id="security-general-type"
                  name="securityType"
                  value={generalForm.securityType}
                  onChange={handleFormChange(setGeneralForm)}
                >
                  <option value="general">General</option>
                  <option value="ssl_certificate">SSL Certificate</option>
                  <option value="domain_protection">Domain Protection</option>
                  <option value="account_security">Account Security</option>
                </select>
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-general-message">Message</label>
                <textarea
                  id="security-general-message"
                  name="message"
                  value={generalForm.message}
                  onChange={handleFormChange(setGeneralForm)}
                  placeholder="Tell us your security requirement"
                  rows={5}
                  required
                />
              </div>

              {generalStatus.message ? (
                <output className={`scta-alert ${generalStatus.type}`}>
                  {generalStatus.message}
                </output>
              ) : null}

              <button type="submit" className="scta-btn" disabled={generalLoading}>
                {generalLoading ? "Submitting..." : "Submit Enquiry"}
              </button>
            </form>
          </div>

          <div className="scta-card" id="security-ssl-form">
            <h3>SSL Request Form</h3>
            <p>Request SSL certificate protection for your website domain.</p>

            <form onSubmit={handleSslSubmit} className="scta-form">
              <div className="scta-form-group">
                <label htmlFor="security-ssl-full-name">Full Name</label>
                <input
                  id="security-ssl-full-name"
                  type="text"
                  name="fullName"
                  value={sslForm.fullName}
                  onChange={handleFormChange(setSslForm)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-ssl-email">Email Address</label>
                <input
                  id="security-ssl-email"
                  type="email"
                  name="email"
                  value={sslForm.email}
                  onChange={handleFormChange(setSslForm)}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-ssl-phone">Phone Number</label>
                <input
                  id="security-ssl-phone"
                  type="text"
                  name="phone"
                  value={sslForm.phone}
                  onChange={handleFormChange(setSslForm)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-ssl-domain">Domain Name</label>
                <input
                  id="security-ssl-domain"
                  type="text"
                  name="domainName"
                  value={sslForm.domainName}
                  onChange={handleFormChange(setSslForm)}
                  placeholder="example.com"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-ssl-message">Message</label>
                <textarea
                  id="security-ssl-message"
                  name="message"
                  value={sslForm.message}
                  onChange={handleFormChange(setSslForm)}
                  placeholder="Any details for SSL setup"
                  rows={4}
                />
              </div>

              {sslStatus.message ? (
                <output className={`scta-alert ${sslStatus.type}`}>
                  {sslStatus.message}
                </output>
              ) : null}

              <button type="submit" className="scta-btn" disabled={sslLoading}>
                {sslLoading ? "Submitting..." : "Request SSL"}
              </button>
            </form>
          </div>
        </div>

        <div className="scta-grid scta-grid-gap">
          <div className="scta-card" id="security-account-form">
            <h3>Account Protection Request</h3>
            <p>Request additional security help for login, account safety, and protection.</p>

            <form onSubmit={handleAccountSubmit} className="scta-form">
              <div className="scta-form-group">
                <label htmlFor="security-account-full-name">Full Name</label>
                <input
                  id="security-account-full-name"
                  type="text"
                  name="fullName"
                  value={accountForm.fullName}
                  onChange={handleFormChange(setAccountForm)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-account-email">Email Address</label>
                <input
                  id="security-account-email"
                  type="email"
                  name="email"
                  value={accountForm.email}
                  onChange={handleFormChange(setAccountForm)}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-account-phone">Phone Number</label>
                <input
                  id="security-account-phone"
                  type="text"
                  name="phone"
                  value={accountForm.phone}
                  onChange={handleFormChange(setAccountForm)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-account-domain">Domain Name</label>
                <input
                  id="security-account-domain"
                  type="text"
                  name="domainName"
                  value={accountForm.domainName}
                  onChange={handleFormChange(setAccountForm)}
                  placeholder="example.com"
                  autoComplete="off"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-account-message">Message</label>
                <textarea
                  id="security-account-message"
                  name="message"
                  value={accountForm.message}
                  onChange={handleFormChange(setAccountForm)}
                  placeholder="Explain the account protection issue"
                  rows={5}
                  required
                />
              </div>

              {accountStatus.message ? (
                <output className={`scta-alert ${accountStatus.type}`}>
                  {accountStatus.message}
                </output>
              ) : null}

              <button type="submit" className="scta-btn" disabled={accountLoading}>
                {accountLoading ? "Submitting..." : "Request Account Protection"}
              </button>
            </form>
          </div>

          <div className="scta-card" id="security-domain-lock-form">
            <h3>Domain Lock Request</h3>
            <p>Protect your domain from unauthorized transfer or ownership changes.</p>

            <form onSubmit={handleDomainLockSubmit} className="scta-form">
              <div className="scta-form-group">
                <label htmlFor="security-lock-full-name">Full Name</label>
                <input
                  id="security-lock-full-name"
                  type="text"
                  name="fullName"
                  value={domainLockForm.fullName}
                  onChange={handleFormChange(setDomainLockForm)}
                  placeholder="Enter your full name"
                  autoComplete="name"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-lock-email">Email Address</label>
                <input
                  id="security-lock-email"
                  type="email"
                  name="email"
                  value={domainLockForm.email}
                  onChange={handleFormChange(setDomainLockForm)}
                  placeholder="Enter your email"
                  autoComplete="email"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-lock-phone">Phone Number</label>
                <input
                  id="security-lock-phone"
                  type="text"
                  name="phone"
                  value={domainLockForm.phone}
                  onChange={handleFormChange(setDomainLockForm)}
                  placeholder="Enter your phone number"
                  autoComplete="tel"
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-lock-domain">Domain Name</label>
                <input
                  id="security-lock-domain"
                  type="text"
                  name="domainName"
                  value={domainLockForm.domainName}
                  onChange={handleFormChange(setDomainLockForm)}
                  placeholder="example.com"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="scta-form-group">
                <label htmlFor="security-lock-message">Message</label>
                <textarea
                  id="security-lock-message"
                  name="message"
                  value={domainLockForm.message}
                  onChange={handleFormChange(setDomainLockForm)}
                  placeholder="Add any notes about domain protection"
                  rows={4}
                />
              </div>

              {domainLockStatus.message ? (
                <output className={`scta-alert ${domainLockStatus.type}`}>
                  {domainLockStatus.message}
                </output>
              ) : null}

              <button
                type="submit"
                className="scta-btn"
                disabled={domainLockLoading}
              >
                {domainLockLoading ? "Submitting..." : "Request Domain Lock"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

export default SecurityCTA;